import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import angular, { IAttributes, IScope } from 'angular'
import ngRedux from 'ng-redux'

import { STATES } from '../../../../../lib/routes'
import { SaleManager } from '../../../managers/sale-manager'
import { SaleStatus } from '../../../models/sale.constants'
import { $log, $q, $rootScope, $state } from '../../../ng-compat/imports'
import { EVENT } from '../../../constants'
import {
  ReduxCurrentSale,
  selectCurrentSale,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { RootState } from '../../../state/store'
import {
  hasCustomerRelatedPayments,
  isAccountBalancePaymentAdded,
  isAccountBalancePaymentPaid,
  isBlindReturn,
  isCompleted,
  isConfirmed,
  isLegacyServiceSale,
  isReturn,
  isUnfulfilled,
} from '../../../functions/sale'
import { hasServiceOrder } from '../../../functions/service'
import { CustomerManager } from '../../../managers/customer-manager'
import { AccountBalanceSetupModalService } from '../../sale/account-balance-setup-modal/account-balance-setup-modal.service'

const DEFAULT_VIEW_OPTIONS = {
  canViewDetails: true,
  hideRemoveFromSale: false,
  hideViewSales: false,
  canAddAccountBalancePayment: false,
}

/**
 * Controller Class for WrCustomerBadge
 */
export class WrCustomerBadgeController {
  public customer: $TSFixMe
  public customerGroup: $TSFixMe
  public viewOptions: $TSFixMe
  public sale: ReduxCurrentSale

  public readonly hasMadePartialCustomerPaymentsTooltip = {
    template: `
      <vd-tooltip class="wr-tooltip">
        <div class="vd-text-label vd-pb2">
          <vd-icon icon="fa-info-circle"></vd-icon>&nbsp;&nbsp;Customer balance used for payment.
        </div>
        This customer can’t be removed from the sale because a partial payment has been made using Loyalty or Store&nbsp;Credit.
      </vd-tooltip>
    `,
    showOnTouch: true,
  }

  public readonly isUnfulfilledSaleTooltip = {
    template: `
      <vd-tooltip class="wr-tooltip">
        <div class="vd-text-label vd-pb2">
          <vd-icon icon="fa-info-circle"></vd-icon>&nbsp;&nbsp;Sale is unfulfilled.
        </div>
        This customer can’t be removed from the sale as the sale is&nbsp;unfulfilled.
      </vd-tooltip>
    `,
    showOnTouch: true,
  }

  public readonly isServiceToolTip = {
    template: `
      <vd-tooltip class="wr-tooltip">
        <div class="vd-text-label vd-pb2">
          <vd-icon icon="fa-info-circle"></vd-icon>&nbsp;&nbsp;Service sale
        </div>
        This customer can’t be removed from a sale that includes a service
      </vd-tooltip>
    `,
    showOnTouch: true,
  }

  public readonly hasMadeAccountBalancePaymentTooltip = {
    template: `
      <vd-tooltip class="wr-tooltip">
        <div class="vd-text-label vd-pb2">
          <vd-icon icon="fa-info-circle"></vd-icon>&nbsp;&nbsp;Account Balance Payment taken.
        </div>
        This customer can’t be removed from the sale as an Account Balance Payment has been&nbsp;taken.
      </vd-tooltip>
    `,
    showOnTouch: true,
  }

  constructor(
    private $attrs: IAttributes,
    private customerManager: CustomerManager,
    private customerModals: $TSFixMe,
    private saleManager: SaleManager,
    private vdTracking: VdTracking,
    private vdFeatureManager: VdFeatureManager,
    private accountBalanceSetupModalService: AccountBalanceSetupModalService,
    private readonly $ngRedux: ngRedux.INgRedux,
    $scope: IScope
  ) {
    'ngInject'
    this.sale = this.$ngRedux.getState().currentSale // typescript doesn't realise that sale is set in the connect function below. This is a workaround to get the initial value of sale to controller then it will be updated by the connect function
    const disconnectRedux = this.$ngRedux.connect((state: RootState) => ({
      sale: selectCurrentSale(state),
    }))(this)
    $scope.$on('$destroy', disconnectRedux)
  }

  public $onInit() {
    this.viewOptions = angular.merge({}, DEFAULT_VIEW_OPTIONS, this.viewOptions)
    this.saleManager.init().then(() => this._setCustomerGroup())
  }

  public $postLink() {
    this.$attrs.$addClass('wr-customer-badge')
  }

  /**
   * Removes the customer currently attached to the sale.
   *
   * @method removeCustomerFromSale
   *
   * @return {Promise<undefined>} Resolved when the customer has been removed from the sale.
   */
  public removeCustomerFromSale() {
    return this.saleManager
      .removeCustomer()
      .then(() => this._focusCustomerSearchIfNeeded())
      .then(() => $rootScope.$emit(EVENT.SALE_UPDATE_TENDERED_AMOUNT))
  }

  /**
   * View customer details.
   *
   * @method viewCustomer
   */
  public viewCustomer() {
    this.vdTracking.trackUserEvent({
      eventName: 'Show Customer Lightbox',
    })
    this.customerModals
      .viewCustomer(this.customer, {
        hideRemoveFromSale: !this.canRemoveCustomerFromSale(),
        hideViewSales: this.viewOptions.hideViewSales,
      })
      .then(() => {
        // Re-focus the global search once the customer modal is closed
        $rootScope.$emit('autocomplete:global-search:focus')

        return this._setCustomerGroup()
      })
  }

  /**
   * Determine if the customer can be removed from the current sale.
   *
   * @method canRemoveCustomerFromSale
   *
   * @return {Boolean} true if the customer can be removed from the sale, false otherwise.
   */
  public canRemoveCustomerFromSale() {
    if (this.viewOptions.hideRemoveFromSale || !this.sale) {
      return false
    }

    const isNewSale = angular.isUndefined(this.sale.status)

    return Boolean(
      this.sale.customer &&
        this.sale.customer.id === this.customer.id &&
        (isNewSale ||
          (!isCompleted(this.sale.status) && !isConfirmed(this.sale))) &&
        !hasCustomerRelatedPayments(this.sale.payments) &&
        !this.unfulfilledSale() &&
        !isAccountBalancePaymentPaid(this.sale.payAccountBalance) &&
        !(
          isLegacyServiceSale(this.sale.status) ||
          hasServiceOrder(this.sale.serviceFields, this.sale.customFields)
        )
    )
  }

  /**
   * Determine if the account balance payment banner should be shown.
   *
   * @method showAccountBalancePaymentBanner
   *
   * @return {Boolean} true if account balance payment should be shown, false otherwise.
   */
  public showAccountBalancePaymentBanner() {
    return Boolean(
      this.viewOptions.canAddAccountBalancePayment &&
        this.sale.status !== SaleStatus.ACCOUNT_SALE &&
        this.sale.status !== SaleStatus.LAYBY_SALE &&
        !isReturn(this.sale) &&
        !isBlindReturn(this.sale) &&
        this.sale.customerBalances?.account &&
        this.sale.customerBalances.account < 0
    )
  }

  /**
   * Open Account Balance Modal
   *
   * @method openAccountBalanceModal
   */
  public openAccountBalanceModal() {
    this.accountBalanceSetupModalService.openModal().then(modal => modal.closed)
  }

  /**
   * Determine if the customer has an account balance payment that they are paying with this sale.
   *
   * @method hasAccountBalancePayment
   *
   * @return {Boolean} true if the attached customer has added an account balance payment, false otherwise.
   */
  public hasAccountBalancePayment() {
    return isAccountBalancePaymentAdded(this.sale.payAccountBalance)
  }

  /**
   * Check if the account balance payment has been paid.
   *
   * @return {Boolean} true if account balance payment has been paid, false otherwise.
   */
  public hasPaidAccountBalancePayment() {
    return isAccountBalancePaymentPaid(this.sale.payAccountBalance)
  }

  /**
   * Get the text to show in the account balance payment banner.
   *
   * @returns {String} the text to show in the account balance payment banner.
   */
  public getAccountBalancePaymentBannerText() {
    if (this.hasAccountBalancePayment()) {
      const text = 'An Account Balance payment has been added'
      return `${text}${
        this.hasPaidAccountBalancePayment() ? ' and paid for' : ''
      }`
    }
    return 'This customer has an unpaid Account Balance'
  }

  /**
   * Determine if the sale is unfulfilled.
   *
   * @method unfulfilledSale
   *
   * @return {Boolean} true if the sale is marked as unfulfilled, false otherwise.
   */
  public unfulfilledSale() {
    return isUnfulfilled(this.sale.status)
  }

  /**
   *  Determine if sale is service order sale or has service order
   *
   * @returns {Boolean} true if the sale is a service order sale, false otherwise
   */
  private serviceOrder() {
    if (!this.sale.serviceFields) {
      return false
    }
    return (
      hasServiceOrder(this.sale.serviceFields, this.sale.customFields) ||
      isLegacyServiceSale(this.sale.status)
    )
  }

  /**
   * Determine if the sale is in progress and the customer has made a partial
   * payment using customer related payments.
   *
   * @method inProgressSaleHasCustomerPayments
   *
   * @return {Boolean} true if the customer has made a partial payment in loyalty or store credit, false otherwise.
   */
  public inProgressSaleHasCustomerPayments() {
    if (this.viewOptions.hideRemoveFromSale || !this.sale) {
      return false
    }

    const isNewSale = angular.isUndefined(this.sale.status)

    return Boolean(
      this.sale.customer &&
        this.sale.customer.id === this.customer.id &&
        (isNewSale ||
          (!isCompleted(this.sale.status) && !isConfirmed(this.sale))) &&
        hasCustomerRelatedPayments(this.sale.payments || [])
    )
  }

  /**
   * Determine if the lock and tooltip should be shown on customer badge
   * for unfulfilled sale.
   *
   * @method showLockForUnfulfilledSale
   *
   * @returns true is sale is unfulfilled, false otherwise.
   */
  public showLockForUnfulfilledSale() {
    return this.unfulfilledSale() && !this.viewOptions.hideRemoveFromSale
  }

  /**
   * Determine if the lock and tooltip should be shown on customer badge
   * for service order.
   *
   * @method showLockForServiceOrder
   *
   * @returns true if the lock and tooltip should be shown
   */
  public showLockForServiceOrder() {
    return (
      this.serviceOrder() &&
      // the unfulfilled sale has a higher priority
      !this.unfulfilledSale() &&
      !this.viewOptions.hideRemoveFromSale
    )
  }

  /**
   * Determine if the lock and tooltip should be shown on customer badge
   * for partial customer payments.
   *
   * @method showLockForPartialCustomerPayments
   *
   * @returns true if customer payment has been made and showLockForUnfulfilledSale() is false,
   * false otherwise.
   */
  public showLockForPartialCustomerPayments() {
    return (
      !this.showLockForUnfulfilledSale() &&
      this.inProgressSaleHasCustomerPayments() &&
      !this.showLockForServiceOrder()
    )
  }

  /**
   * Determine if the lock and tooltip should be shown on customer badge
   * for account balance payments.
   *
   * @method showLockForAccountBalancePayment
   *
   * @returns true if there is a paid account balance payment on this sale and
   * showLockForUnfulfilledSale() and both false showLockForPartialCustomerPayments(),
   * false otherwise.
   */
  public showLockForAccountBalancePayment() {
    return (
      !this.showLockForUnfulfilledSale() &&
      !this.showLockForPartialCustomerPayments() &&
      isAccountBalancePaymentPaid(this.sale.payAccountBalance) &&
      !this.viewOptions.hideRemoveFromSale &&
      !this.showLockForServiceOrder()
    )
  }

  /**
   * Focuses the customer search autocomplete if on the Sale state.
   *
   * @private
   * @method _focusCustomerSearchIfNeeded
   */
  private _focusCustomerSearchIfNeeded() {
    if (!$state.is(STATES.SALE)) {
      return
    }

    $rootScope.$emit('autocomplete:customer-search:focus', {
      isFocusable: true,
    })
  }

  /**
   * Retrieves the group details for the current customer.
   *
   * @private
   * @method _setCustomerGroup
   *
   * @return {Promise<undefined>} Resolved when the group for the customer has been set.
   */
  private _setCustomerGroup() {
    if (!this.customer) {
      return $q.resolve()
    }
    if (!this.vdFeatureManager.get('customer_groups')) {
      this.customerGroup = undefined
      return
    }
    return this.customerManager
      .getCustomerGroup(this.customer)
      .then(customerGroup => {
        this.customerGroup = customerGroup
      })
      .catch(error => {
        $log.warn('[WrCustomerBadge] Failed to lookup customer group', error)
      })
  }
}
